import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { sendSMS } from '../../../services/auth';
import './MobileLogin.css';

const SMS_COOLDOWN = 60;

export default function MobileLogin() {
  const [phone, setPhone]             = useState('');
  const [code, setCode]               = useState('');
  const [inviteCode, setInviteCode]   = useState('');
  const [showInvite, setShowInvite]   = useState(false);
  const [agreeTerms, setAgreeTerms]   = useState(false);
  const [countdown, setCountdown]     = useState(0);
  const [sending, setSending]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const countdownRef = useRef(null);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, redirect
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || '/m';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // Cleanup timer on unmount
  useEffect(() => () => clearInterval(countdownRef.current), []);

  const startCountdown = () => {
    setCountdown(SMS_COOLDOWN);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendSMS = async () => {
    if (!phone || phone.length < 11) { setError('请输入正确的手机号'); return; }
    setSending(true);
    setError('');
    try {
      const result = await sendSMS(phone);
      if (result.ok) {
        startCountdown();
      } else {
        setError(result.msg || '发送失败，请重试');
      }
    } catch {
      setError('网络错误，请重试');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!phone) { setError('请输入手机号'); return; }
    if (!code)  { setError('请输入验证码'); return; }
    if (!agreeTerms) { setError('请先同意服务条款'); return; }

    setSubmitting(true);
    setError('');
    try {
      const result = await login({ phone, code, inviteCode: inviteCode || undefined });
      if (result.success) {
        const from = location.state?.from || '/m';
        navigate(from, { replace: true });
      } else {
        setError(result.error || '登录失败，请检查验证码');
      }
    } catch {
      setError('网络错误，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mlogin-root">
      {/* Logo */}
      <div className="mlogin-logo">
        <img src="/logo-mobile-mark.svg" alt="PSYCHE-AI" />
        <span className="mlogin-logo-name">PSYCHE-AI</span>
        <span className="mlogin-logo-sub">数字人视频创作平台</span>
      </div>

      {/* Card */}
      <div className="mlogin-card">
        <div className="mlogin-card-title">手机号登录</div>

        {/* Phone */}
        <div className="mlogin-field">
          <label className="mlogin-label" htmlFor="m-phone">手机号</label>
          <input
            id="m-phone"
            className="mlogin-input"
            type="tel"
            inputMode="numeric"
            placeholder="请输入手机号"
            maxLength={11}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        {/* SMS code */}
        <div className="mlogin-field">
          <label className="mlogin-label" htmlFor="m-code">验证码</label>
          <div className="mlogin-sms-row">
            <input
              id="m-code"
              className="mlogin-input"
              type="text"
              inputMode="numeric"
              placeholder="请输入验证码"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <button
              className="mlogin-sms-btn"
              onClick={handleSendSMS}
              disabled={countdown > 0 || sending}
            >
              {sending ? '发送中' : countdown > 0 ? `${countdown}s` : '获取验证码'}
            </button>
          </div>
        </div>

        {/* Invite code (optional) */}
        <div
          className="mlogin-optional-row"
          onClick={() => setShowInvite((p) => !p)}
        >
          <span className="mlogin-optional-label">邀请码（选填）</span>
          <span className={`mlogin-optional-arrow${showInvite ? ' open' : ''}`}>▶</span>
        </div>
        {showInvite && (
          <div className="mlogin-field">
            <input
              className="mlogin-input"
              type="text"
              placeholder="请输入邀请码"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
            />
          </div>
        )}

        {/* Terms */}
        <div className="mlogin-terms-row">
          <input
            id="m-terms"
            type="checkbox"
            className="mlogin-terms-check"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <label className="mlogin-terms-text" htmlFor="m-terms">
            我已阅读并同意《用户协议》和《隐私政策》
          </label>
        </div>

        {/* Submit */}
        <button
          className="mlogin-submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <span className="mlogin-spin" /> : '登录 / 注册'}
        </button>

        {/* Error */}
        {error && <div className="mlogin-error">{error}</div>}
      </div>
    </div>
  );
}
